import React from "react";
import { Counterparty } from "@common/types/counterparty";
import { useCounterpartyDetailsQuery } from "@api/counterparty.api";
import { HStack } from "@design-system/HStack/HStack";
import { Tag } from "@design-system/Tag/Tag";
import { DescriptionListItem } from "@design-system/DescriptionList/DescriptionList";
import {
    formatCounterpartyAddressSummary,
    formatCounterpartyContactHeadline,
} from "@components/counterparty/counterpartyBookFormat";
import panelStyles from "../CounterpartyDetailsPanel.module.scss";

interface BookEntry {
    id: string;
    text: string;
    isPrimary: boolean;
}

interface BookEntryListProps {
    entries: BookEntry[];
    emptyLabel: string;
    isLoading: boolean;
}

const BookEntryList: React.FC<BookEntryListProps> = ({
    entries,
    emptyLabel,
    isLoading,
}) => (
    <div className={panelStyles.value}>
        {entries.length === 0 && !isLoading && (
            <span className={panelStyles.emptyValue}>{emptyLabel}</span>
        )}
        {entries.map((entry) => (
            <HStack key={entry.id} gap={6} align="start">
                <span>{entry.text}</span>
                {entry.isPrimary && (
                    <Tag color="neutral" size="sm">
                        Primary
                    </Tag>
                )}
            </HStack>
        ))}
    </div>
);

interface Props {
    counterparty: Counterparty;
}

/** Company, contact and address rows for the counterparty details list. */
export const CounterpartyBooksSummary: React.FC<Props> = ({ counterparty }) => {
    const detailsQuery = useCounterpartyDetailsQuery({
        counterpartyId: counterparty.id,
    });
    const contacts = detailsQuery.data?.contacts ?? [];
    const addresses = detailsQuery.data?.addresses ?? [];

    return (
        <>
            <CompanyFields counterparty={counterparty} />
            <DescriptionListItem label="Contacts" alignTop>
                <BookEntryList
                    entries={contacts.map((contact) => ({
                        id: contact.id,
                        text: formatCounterpartyContactHeadline(contact),
                        isPrimary: contact.isPrimary,
                    }))}
                    emptyLabel="No contacts"
                    isLoading={detailsQuery.isLoading}
                />
            </DescriptionListItem>
            <DescriptionListItem label="Addresses" alignTop>
                <BookEntryList
                    entries={addresses.map((address) => ({
                        id: address.id,
                        text: formatCounterpartyAddressSummary(address),
                        isPrimary: address.isPrimary,
                    }))}
                    emptyLabel="No addresses"
                    isLoading={detailsQuery.isLoading}
                />
            </DescriptionListItem>
        </>
    );
};

const CompanyFields: React.FC<{ counterparty: Counterparty }> = ({
    counterparty,
}) => {
    const rows = [
        { label: "Company", value: counterparty.companyName },
        { label: "Website", value: counterparty.website },
        { label: "CC", value: counterparty.cc },
        { label: "BCC", value: counterparty.bcc },
    ].filter((row) => Boolean(row.value));

    return rows.map((row) => (
        <DescriptionListItem key={row.label} label={row.label}>
            <span className={panelStyles.value}>{row.value}</span>
        </DescriptionListItem>
    ));
};
